import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMcpConnections, useReloadMcp } from '@/lib/api/mcp-connections';

const PAGE_SIZE = 20;

export function McpConnectionsListPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const connectionsQuery = useMcpConnections({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
  });
  const reload = useReloadMcp();

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const result = connectionsQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">MCP connections</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Remote MCP servers our client connects to, over Streamable HTTP.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={reload.isPending} onClick={() => reload.mutate()}>
            {reload.isPending ? 'Reloading…' : 'Reload'}
          </Button>
          <Button asChild>
            <Link to="/mcp/connections/new">
              <Plus className="size-4" />
              New connection
            </Link>
          </Button>
        </div>
      </div>

      <Input
        placeholder="Search by name or id…"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        className="h-8 w-full sm:w-72"
      />

      {connectionsQuery.isPending && <p className="text-sm">Loading…</p>}
      {connectionsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load connections: {connectionsQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((connection) => (
                    <TableRow key={connection.id}>
                      <TableCell>
                        <Link
                          to="/mcp/connections/$connectionId"
                          params={{ connectionId: connection.id }}
                          className="font-medium hover:underline"
                        >
                          {connection.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">{new URL(connection.url).host}</TableCell>
                      <TableCell>
                        {connection.enabled ? (
                          <Badge variant="secondary">Enabled</Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {connection.status.connected ? (
                          <Badge variant="secondary">
                            Connected ({connection.status.liveToolCount})
                          </Badge>
                        ) : (
                          <Badge variant="outline">Disconnected</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center">
                      No MCP connections yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {result.total} connection(s)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => value + 1)}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
